import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { format } from 'date-fns';
import './EventDetail.css';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

function EventDetail() {
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadEvent = async () => {
      try {
        setLoading(true);
        const res = await fetch(`${API_URL}/api/events/${id}`);
        
        if (!res.ok) {
          throw new Error('Event not found');
        }
        
        const data = await res.json();
        setEvent(data);
        setError(null);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    loadEvent();
  }, [id]);

  if (loading) {
    return (
      <div className="container">
        <div className="loading">
          <div className="spinner"></div>
          <span style={{ marginLeft: '12px' }}>Loading event...</span>
        </div>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="container">
        <div className="error">
          <h2>Event Not Found</h2>
          <p>{error || 'The requested event could not be loaded.'}</p>
          <Link to="/" className="btn btn-primary" style={{ marginTop: '16px' }}>Back to Feed</Link>
        </div>
      </div>
    );
  }

  const confidenceLevel = event.confidence_score >= 0.85 ? 'high' : 
                          event.confidence_score >= 0.60 ? 'medium' : 'low';
  const confidenceText = event.confidence_score >= 0.85 ? 'Verified' : 
                         event.confidence_score >= 0.60 ? 'Emerging' : 'Developing';

  return (
    <div className="container">
      <div className="event-detail">
        <Link to="/" className="back-link">
          ← Back to Feed
        </Link>

        <div className="event-header">
          <div className="header-badges">
            <div className={`confidence-badge ${confidenceLevel}`}>
              {confidenceText} {Math.round(event.confidence_score * 100)}%
            </div>
          </div>
          
          <h1 className="event-title">{event.title}</h1>
          
          <div className="event-metadata">
            <span>🔥 Detected {format(new Date(event.detected_at), 'MMMM d, yyyy • HH:mm')}</span>
            <span>📊 {event.source_count} sources</span>
            <span>🌐 {event.platforms?.join(', ')}</span>
          </div>
        </div>

        {event.summary && (
          <div className="event-summary-card card">
            <h3 className="section-title">Summary</h3>
            <p>{event.summary}</p>
          </div>
        )}

        <div className="confidence-section card">
          <h3 className="section-title">Event Confidence Analysis</h3>
          <div className="confidence-bar-container">
            <div 
              className={`confidence-bar ${confidenceLevel}`}
              style={{ width: `${event.confidence_score * 100}%` }}
            >
              <span className="confidence-percentage">
                {Math.round(event.confidence_score * 100)}%
              </span>
            </div>
          </div>
          <div className="confidence-factors">
            <div className="factor">
              <span className="factor-label">Sources Analyzed:</span>
              <span className="factor-value">{event.source_count}</span>
            </div>
            <div className="factor">
              <span className="factor-label">Platforms:</span>
              <span className="factor-value">{event.platforms?.join(', ')}</span>
            </div>
            <div className="factor">
              <span className="factor-label">Detection Method:</span>
              <span className="factor-value">BIRCH Clustering</span>
            </div>
          </div>
        </div>

        <div className="clustered-posts card">
          <h3 className="section-title">
            🔗 Clustered Posts
          </h3>
          <p className="section-description">
            These {event.source_count} posts were grouped together by our AI clustering algorithm 
            as part of the same breaking news event.
          </p>
          
          <div className="posts-info">
            <p>Source post IDs are stored in the system. In a production deployment, 
            these would be expanded to show the full post content with links to originals.</p>
            
            {event.source_post_ids && event.source_post_ids.length > 0 && (
              <div className="post-ids">
                <h4>Source Post References:</h4>
                <ul>
                  {event.source_post_ids.slice(0, 5).map((postId, idx) => (
                    <li key={idx}>
                      <code>{postId}</code>
                    </li>
                  ))}
                  {event.source_post_ids.length > 5 && (
                    <li>...and {event.source_post_ids.length - 5} more</li>
                  )}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default EventDetail;
